import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Asset, Site, SiteArea } from 'graphql-common';
import AppContentMaxWidth from '@lib/layouts/AppLayout/AppContentMaxWidth/AppContentMaxWidth';
import Form, { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import formStyles from '@lib/assets/styles/Form.module.scss';
import TopNavigationHeader from '@lib/layouts/AppLayout/TopNavigationHeader/TopNavigationHeader';
import { APP_URLS } from 'constants/urls';
import { ScannerActionType } from 'routes/Scanner/enums';
import ScanQrStickyBtn from 'components/ScanQrStickyBtn/ScanQrStickyBtn';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import FieldNames from '@lib/enums/fieldNames/interventionFieldNames';
import TaskAndCompletionsModal from '@lib/components/TaskCompletions/TaskAndCompletionsModal/TaskAndCompletionsModal';
import useTaskAndCompletionsPicker from '@lib/components/TaskCompletions/TaskAndCompletionsModal/useTaskAndCompletionsPicker';
import useInterventionLinkedCompletions from '@lib/components/TaskCompletions/utils/useInterventionLinkedCompletions';
import useTasksTableMultiselectFilterLoadOptions from 'utils/fetch/hooks/useTasksTableMultiselectFilterLoadOptions';
import { taskCompletionsModalUrls } from 'components/TaskCompletionsModal/TaskCompletionsModal';
import getInterventionFormFields from '@lib/utils/intervention/getInterventionFormFields';
import useSearchListQueries from '../utils/useSearchListQueries';
import getInterventionDefaultValuesFromQueryData from '../utils/getInterventionDefaultValuesFromQueryData';
import { UseInterventionActionsResult } from '../utils/useInterventionActions';
import getInterventionDefaultValues from '../utils/getInterventionDefaultValues';
import { InterventionViewMode } from '../enums';

type Props = {
  useInterventionActionsResult: UseInterventionActionsResult;
  viewMode: InterventionViewMode;
  isTasksAvailable: boolean;
};

export default function InterventionFormPage({
  useInterventionActionsResult,
  viewMode,
  isTasksAvailable,
}: Props) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const navigate = useNavigate();
  const useSearchListQueriesResult = useSearchListQueries();
  const {
    id,
    mutationError,
    interventionQueryHookResult,
    loading,
    onSubmit,
    usersQueryHookResult,
    interventionSitePrefillDataQueryHookResult,
    interventionSiteAreaPrefillDataQueryHookResult,
    interventionAssetPrefillDataQueryHookResult,
  } = useInterventionActionsResult;
  const { allIds: assigneeIds } = usersQueryHookResult;
  const taskAndCompletionsPicker = useTaskAndCompletionsPicker(formRef);
  const taskFilterLoadOptions = useTasksTableMultiselectFilterLoadOptions();

  const isCreatePage = viewMode === InterventionViewMode.Add;

  const linkedCompletions = useInterventionLinkedCompletions({
    interventionId: id,
    taskGroupKey: interventionQueryHookResult.data?.task?.groupKey,
    skip: isCreatePage || !isTasksAvailable,
  });

  const defaultValues = isCreatePage
    ? getInterventionDefaultValues({
        site: interventionSitePrefillDataQueryHookResult?.data?.site as Site,
        siteArea: interventionSiteAreaPrefillDataQueryHookResult?.data
          ?.siteArea as SiteArea,
        asset: interventionAssetPrefillDataQueryHookResult?.data
          ?.asset as Asset,
      })
    : getInterventionDefaultValuesFromQueryData(
        interventionQueryHookResult.data,
        assigneeIds,
        linkedCompletions.options,
      );

  const fields = getInterventionFormFields({
    assigneeIds,
    interventionId: id,
    t,
    isEditForm: viewMode === InterventionViewMode.Edit,
    isTasksAvailable,
    getAssetsLoadOptions: useSearchListQueriesResult.getAssetsLoadOptions,
    getInterventionCategoriesLoadOptions:
      useSearchListQueriesResult.getInterventionCategoriesLoadOptions,
    getSiteAreasLoadOptions: useSearchListQueriesResult.getSiteAreasLoadOptions,
    getSitesLoadOptions: useSearchListQueriesResult.getSitesLoadOptions,
    getSupervisorLoadOptions:
      useSearchListQueriesResult.getSupervisorLoadOptions,
    getAssigneeLoadOptions: useSearchListQueriesResult.getAssigneeLoadOptions,
    getTasksLoadOptions: useSearchListQueriesResult.getTasksLoadOptions,
    openTaskStep: taskAndCompletionsPicker.openTaskStep,
    openCompletionsStep: taskAndCompletionsPicker.openCompletionsStep,
    onSiteChange: taskAndCompletionsPicker.onSiteChange,
    onSiteAreaChange: taskAndCompletionsPicker.onSiteAreaChange,
    onAssetChange: taskAndCompletionsPicker.onAssetChange,
    showOfflineMessage: true,
    hideAssigneeListErrorsSpace: true,
  });

  const isFormLoading = loading || linkedCompletions.loading;

  const actionsComponent = () => (
    <ActionsRow
      className={formStyles.formActions}
      actions={[
        <Button
          onClick={() => navigate(-1)}
          buttonType={ButtonTypes.secondaryOutlined}
          buttonText={t('cancel')}
          leftIcon="close"
          fullWidth
          data-test-id="intervention-form__cancel-button"
        />,
        <Button
          buttonType={ButtonTypes.primaryFilled}
          buttonText={t(isCreatePage ? 'create' : 'save-changes')}
          leftIcon="check"
          type="submit"
          disabled={isFormLoading}
          isLoading={isFormLoading}
          fullWidth
          data-test-id="intervention-form__save-button"
        />,
      ]}
    />
  );

  return (
    <>
      <TopNavigationHeader
        title={t(isCreatePage ? 'create-intervention' : 'edit-intervention')}
        backUrl={
          isCreatePage
            ? APP_URLS.app.interventions.index.getPathWithQuery()
            : APP_URLS.app.interventions.view.getDynamicPath({
                pathParts: { id },
              })
        }
      />
      <PageBody withoutHeader>
        <AppContentMaxWidth>
          <Form
            actionsComponent={actionsComponent}
            actionsComponentProps={{ isCreatePage }}
            defaultValues={defaultValues}
            fields={fields}
            formId="intervention"
            formRef={formRef}
            graphQLErrors={getGraphQLErrors(mutationError)}
            graphQLErrorsReplacePathMap={{
              taskCompletions: FieldNames.Completions,
            }}
            loading={isFormLoading}
            onSubmit={onSubmit}
          />
        </AppContentMaxWidth>
        {isCreatePage && (
          <ScanQrStickyBtn
            scannerActionType={ScannerActionType.CreateIntervention}
          />
        )}
      </PageBody>
      {isTasksAvailable && (
        <TaskAndCompletionsModal
          {...taskAndCompletionsPicker.modalProps}
          taskFilterLoadOptions={taskFilterLoadOptions}
          completionUrls={taskCompletionsModalUrls}
          hideCompletionPreview
        />
      )}
    </>
  );
}
